import { Trans } from '@lingui/react/macro';
import type Highcharts from 'highcharts';
import { type Options } from 'highcharts';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import { type PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

import AutoSizer from '@/components/AutoSizer';
import { Empty } from '@/components/Empty';
import WidgetError from '@/components/errors/WidgetError';
import { SpinnerOverlay } from '@/components/loaders';
import { VISUALISATION_CHART_COLORS } from '@/constants/colors';
import useHighchartsHover from '@/hooks/highcharts/useHighchartsHover';
import useHighchartsHoverLeave from '@/hooks/highcharts/useHighchartsHoverLeave';
import useControlled from '@/hooks/useControlled';
import { cn } from '@/lib/utils';
import { COLOR_AQUA_400, COLOR_GREEN_500 } from '@/styles/palette';

import { FlareContextProvider } from './FlareContext';
import HighchartsRenderer from './HighchartsRenderer';
import {
  type BuildOptionsFunction,
  type FlareChart,
  type FlareProps,
  type FlareSeriesOptions,
} from './types';
import initializeOptions from './utils/initialize-options';

type ChildrenMapType = Map<string, BuildOptionsFunction>;

const Flare = ({
  style = {},
  className = '',
  callback,
  postRender,
  children,
  data,
  description,
  emptyMessage = <Trans>No Data Available</Trans>,
  subtitle,
  title,
  paddingTop = 0,
  paddingBottom = 0,
  paddingLeft = 0,
  paddingRight = 0,
  marginTop = 0,
  marginBottom = 0,
  marginLeft = 0,
  marginRight = 0,
  spacingTop = 24,
  spacingBottom = 20,
  spacingLeft = 32,
  spacingRight = 32,
  chartMarginTop,
  chartMarginBottom,
  chartMarginLeft,
  chartMarginRight,
  height = 'auto',
  width = 'auto',
  options,
  parseX = (val) => val,
  parseY = (val) => val,
  colors = VISUALISATION_CHART_COLORS,
  textColors = [COLOR_GREEN_500, COLOR_AQUA_400],
  inverted = false,
  noPadding = false,
  debug = false,
  isLoading = false,
  error,
  defaultDisabledSeriesIds = [],
  disabledSeriesIds: disabledSeriesIdsProp,
  'data-testid': dataTestId,
  onHoverChart,
  onHoverLeaveChart,
  onDisabledSeriesChange,
}: PropsWithChildren<FlareProps>) => {
  const [disabledSeriesIds, setDisabledSeriesIds] = useControlled(
    disabledSeriesIdsProp,
    defaultDisabledSeriesIds,
    onDisabledSeriesChange,
  );

  // Hang on to the last successful response and don't reset the chart when we go to `loading`
  // state and `data == null`. Instead, just display a small spinner over the stale data.
  const stickyDataSource = useRef<FlareSeriesOptions[] | undefined>(undefined);
  const stickyOptions = useRef<Options>(undefined);

  const chartRef = useRef<HighchartsReactRefObject>(null);
  const flareChart = chartRef.current?.chart as FlareChart | undefined;
  const [registeredChildren, setRegisteredChildren] = useState<ChildrenMapType>(new Map());
  const needsAutoSize = width === 'auto' || height === 'auto';

  useHighchartsHover(flareChart, onHoverChart);
  useHighchartsHoverLeave(flareChart, onHoverLeaveChart);

  const registerChild = useCallback(
    (id: string, buildOptions: BuildOptionsFunction) => {
      if (!registeredChildren.has(id)) {
        setRegisteredChildren((prevChildren) => {
          const newChildren = new Map(prevChildren);
          newChildren.set(id, buildOptions);
          return newChildren;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRegisteredChildren],
  );

  if (data) {
    stickyDataSource.current = data;
  }

  if (error) {
    // reset stickyDataSource when error happens so we can show the message on an empty single row
    stickyDataSource.current = undefined;
  }

  const showSkeletonLoader = isLoading && stickyDataSource.current == null;
  const showSpinnerLoader = isLoading && stickyDataSource.current != null;

  const parentProps = useMemo(
    () => ({
      data: stickyDataSource.current,
      colors,
      textColors,
      parseX,
      parseY,
      isLoading: showSkeletonLoader,
      disabledSeriesIds,
    }),
    [stickyDataSource, colors, textColors, parseX, parseY, showSkeletonLoader, disabledSeriesIds],
  );

  const finalOptions = useMemo(() => {
    if (showSpinnerLoader) {
      // "freeze" the chart options to look like the last successful render
      return stickyOptions.current;
    }

    const initialOptions = initializeOptions(options, {
      title,
      subtitle,
      description,
      colors,
      inverted,
      chartMarginTop,
      chartMarginBottom,
      chartMarginLeft,
      chartMarginRight,
      spacingTop,
      spacingBottom,
      spacingLeft,
      spacingRight,
      postRender,
    });
    const fullOptions: Highcharts.Options = [...registeredChildren.values()].reduce(
      (memo, childBuildOptions) => childBuildOptions(memo, parentProps),
      initialOptions,
    );

    if (debug) {
      console.info(fullOptions);
    }

    stickyOptions.current = fullOptions;
    return fullOptions;
  }, [
    registeredChildren,
    showSpinnerLoader,
    options,
    title,
    subtitle,
    description,
    colors,
    inverted,
    chartMarginTop,
    chartMarginBottom,
    chartMarginLeft,
    chartMarginRight,
    spacingTop,
    spacingBottom,
    spacingLeft,
    spacingRight,
    postRender,
    debug,
    parentProps,
  ]);

  const isEmpty =
    (stickyDataSource.current == null || stickyDataSource.current.length === 0) &&
    !error &&
    !isLoading &&
    !options?.series?.length;

  return (
    <SpinnerOverlay
      className="flex h-full flex-1 flex-col overflow-auto"
      isSpinning={!!showSpinnerLoader && !error}
    >
      <div className={cn('flex h-full flex-col', className)} style={style} data-testid={dataTestId}>
        <FlareContextProvider
          value={{
            registerChild,
            parentProps,
            options: finalOptions,
            isLoading: showSkeletonLoader,
            disabledSeriesIds: disabledSeriesIds,
            setDisabledSeriesIds,
            chart: chartRef.current?.chart as FlareChart,
          }}
        >
          {error ? <WidgetError /> : isEmpty ? <Empty>{emptyMessage}</Empty> : children}
          {!showSkeletonLoader && !error && (
            <div
              className={cn(
                'flare-chart-wrapper relative m-0 flex-1',
                noPadding && 'm-0',
                needsAutoSize && '[&>div]:absolute [&>div]:inset-0 [&>div]:size-full!',
              )}
              style={{
                paddingLeft,
                paddingRight,
                paddingTop,
                paddingBottom,
                marginLeft,
                marginRight,
                marginTop,
                marginBottom,
              }}
            >
              {needsAutoSize ? (
                <AutoSizer doNotBailOutOnEmptyChildren>
                  {({ width: measuredWidth, height: measuredHeight }) => (
                    <HighchartsRenderer
                      ref={chartRef}
                      callback={callback}
                      options={finalOptions}
                      measuredWidth={width === 'auto' ? measuredWidth : width}
                      measuredHeight={height === 'auto' ? measuredHeight : height}
                      disabledSeriesIds={disabledSeriesIds}
                    />
                  )}
                </AutoSizer>
              ) : (
                <HighchartsRenderer
                  ref={chartRef}
                  callback={callback}
                  options={finalOptions}
                  disabledSeriesIds={disabledSeriesIds}
                />
              )}
            </div>
          )}
        </FlareContextProvider>
      </div>
    </SpinnerOverlay>
  );
};

export default Flare;
